import React, { useState, useRef, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./SmartQuestionaire.css";
import { createSmartQuestionaireForm, createNormalQuestionaireForm, editSmartQuestionaireForm, editNormalQuestionaireForm } from "../../../services/CommonService";
import { toast } from "react-toastify";
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const QUESTION_TYPES = [
  { value: "text", label: "Text", icon: "bi-type", id: 1 },
  { value: "number", label: "Number", icon: "bi-123", id: 2 },
  { value: "email", label: "Email", icon: "bi-envelope", id: 3 },
  { value: "textarea", label: "Textarea", icon: "bi-text-paragraph", id: 4 },
  { value: "radio", label: "Radio", icon: "bi-record-circle", id: 5 },
  { value: "checkbox", label: "Checkbox", icon: "bi-check-square", id: 6 },
  { value: "date", label: "Date", icon: "bi-calendar", id: 7 },
];

const DragHandleIcon = () => (
  <svg width="20" height="28" viewBox="0 0 20 28" fill="none" xmlns="http://www.w3.org/2000/svg" className="drag-handle-icon">
    <circle cx="5" cy="5" r="2" fill="#6c757d" />
    <circle cx="5" cy="14" r="2" fill="#6c757d" />
    <circle cx="5" cy="23" r="2" fill="#6c757d" />
    <circle cx="15" cy="5" r="2" fill="#6c757d" />
    <circle cx="15" cy="14" r="2" fill="#6c757d" />
    <circle cx="15" cy="23" r="2" fill="#6c757d" />
  </svg>
);

function FormBuilderOffcanvas({ show,
  onClose,
  sleeve_uuid = '',
  onFormSaved,
  loading,
  setLoading,
  editMode,
  editFormData,
  formObj,
  questionnaireType = 'smart' }) {
  const [formName, setFormName] = useState("");
  const [groupName, setGroupName] = useState("");
  const [groups, setGroups] = useState([]);
  const [selectedGroupIdx, setSelectedGroupIdx] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [groupNameError, setGroupNameError] = useState("");
  const closeButtonRef = useRef(null);
  const [editingGroupIdx, setEditingGroupIdx] = useState(null);
  const [editingGroupName, setEditingGroupName] = useState("");

  useEffect(() => {
    if (editMode && editFormData) {
      setFormName(editFormData.form_name || editFormData.smart_form_name || "");
      setGroups(
        (editFormData.groups || []).map((g) => ({
          name: g.group_name || g.name || `Group ${g.group_id || ''}`,
          group_id: g.group_id, // Preserve group_id for edit operations
          questions: (g.questions || []).map((q) => ({
            type: q.question_type || QUESTION_TYPES.find(t => t.id === q.question_type_id)?.value || "text",
            label: q.question_text || q.label || "",
            options: q.options ? q.options.map((opt) => ({
              name: opt.name || "",
              option_id: opt.option_id || opt.id // Preserve option_id for edit operations
            })) : [],
            selectedOption: q.selectedOption || null,
            is_required: q.is_required || false,
            question_id: q.question_id, // Preserve question_id for edit operations
          })),
        }))
      );
      setSelectedGroupIdx(0);
    } else {
      setFormName("");
      setGroups([]);
      setSelectedGroupIdx(null);
    }
  }, [editMode, editFormData]);


  useEffect(() => {
    if (!show) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [show, onClose]);

  const clearForm = () => {
    setFormName("");
    setGroupName("");
    setGroups([]);
    setSelectedGroupIdx(null);
  };

  const handleAddGroup = () => {
    if (!groupName.trim()) {
      setGroupNameError("Group name is required");
      return;
    }
    if (groups.some(g => g.name.toLowerCase() === groupName.toLowerCase())) {
      setGroupNameError("Group name must be unique");
      return;
    }
    setGroupNameError("");
    setGroups([...groups, { name: groupName, questions: [], group_id: null }]); // New groups don't have group_id
    setGroupName("");
    setSelectedGroupIdx(groups.length);
  };

  const handleGroupNameChange = (idx, value) => {
    setGroups(groups.map((g, i) => (i === idx ? { ...g, name: value } : g)));
  };

  const handleAddQuestion = () => {
    if (selectedGroupIdx === null) {
      toast.error("Please select a group first");
      return;
    }
    setGroups(groups.map((g, i) =>
      i === selectedGroupIdx
        ? { ...g, questions: [...g.questions, { type: "text", label: "", selectedOption: null, is_required: false, question_id: null }] } // New questions don't have question_id
        : g
    ));
  };

  const handleQuestionChange = (qIdx, field, value) => {
    setGroups(groups.map((g, i) => {
      if (i !== selectedGroupIdx) return g;
      const questions = g.questions.map((q, j) => {
        if (j !== qIdx) return q;
        if (field === 'type' && value === 'radio') {
          return { ...q, [field]: value, options: [{ name: "" }], selectedOption: null, is_required: q.is_required }; // New options for type change don't have option_id
        }
        if (field === 'type' && value === 'checkbox') {
          return { ...q, [field]: value, options: [{ name: "" }], selectedOption: [], is_required: q.is_required }; // New options for type change don't have option_id
        }
        return { ...q, [field]: value, is_required: q.is_required };
      });
      return { ...g, questions };
    }));
  };

  const handleAddOption = (qIdx) => {
    setGroups(groups.map((g, i) => {
      if (i !== selectedGroupIdx) return g;
      const questions = g.questions.map((q, j) => {
        if (j !== qIdx) return q;
        return {
          ...q,
          options: [...(q.options || []), { name: "" }] // New options don't have option_id
        };
      });
      return { ...g, questions };
    }));
  };

  const handleOptionChange = (qIdx, optionIdx, value) => {
    setGroups(groups.map((g, i) => {
      if (i !== selectedGroupIdx) return g;
      const questions = g.questions.map((q, j) => {
        if (j !== qIdx) return q;
        const options = q.options?.map((opt, k) =>
          k === optionIdx ? { ...opt, name: value } : opt // Preserve option_id when updating name
        ) || [];
        return { ...q, options };
      });
      return { ...g, questions };
    }));
  };

  const handleDeleteOption = (qIdx, optionIdx) => {
    setGroups(groups.map((g, i) => {
      if (i !== selectedGroupIdx) return g;
      const questions = g.questions.map((q, j) => {
        if (j !== qIdx) return q;
        const options = q.options?.filter((_, k) => k !== optionIdx) || [];
        return { ...q, options };
      });
      return { ...g, questions };
    }));
  };

  const handleDeleteQuestion = (qIdx) => {
    setGroups(groups.map((g, i) => {
      if (i !== selectedGroupIdx) return g;
      const questions = g.questions.filter((_, j) => j !== qIdx);
      return { ...g, questions };
    }));
  };

  const handleSelectGroup = (idx) => {
    setSelectedGroupIdx(idx);
  };

  const handleDeleteGroup = (idx) => {
    const newGroups = groups.filter((_, i) => i !== idx);
    setGroups(newGroups);
    if (selectedGroupIdx === idx) setSelectedGroupIdx(null);
    else if (selectedGroupIdx > idx) setSelectedGroupIdx(selectedGroupIdx - 1);
  };

  const handleRadioSelect = (qIdx, optionIdx) => {
    setGroups(groups.map((g, i) => {
      if (i !== selectedGroupIdx) return g;
      const questions = g.questions.map((q, j) => {
        if (j !== qIdx) return q;
        return { ...q, selectedOption: optionIdx };
      });
      return { ...g, questions };
    }));
  };

  const handleCheckboxSelect = (qIdx, optionIdx) => {
    setGroups(groups.map((g, i) => {
      if (i !== selectedGroupIdx) return g;
      const questions = g.questions.map((q, j) => {
        if (j !== qIdx) return q;
        let selected = Array.isArray(q.selectedOption) ? [...q.selectedOption] : [];
        if (selected.includes(optionIdx)) {
          selected = selected.filter(idx => idx !== optionIdx);
        } else {
          selected.push(optionIdx);
        }
        return { ...q, selectedOption: selected };
      });
      return { ...g, questions };
    }));
  };

  const handleRequiredToggle = (qIdx) => {
    setGroups(groups.map((g, i) => {
      if (i !== selectedGroupIdx) return g;
      const questions = g.questions.map((q, j) => {
        if (j !== qIdx) return q;
        return { ...q, is_required: !q.is_required };
      });
      return { ...g, questions };
    }));
  };

  const validateFormData = () => {
    if (!formName.trim()) {
      toast.error("Please enter a form name");
      return false;
    }
    if (groups.length === 0) {
      toast.error("Please add at least one group");
      return false;
    }
    if (groups.some(group => group.questions.length === 0)) {
      toast.error("All groups must have at least one question");
      return false;
    }
    if (groups.some(group => group.questions.some(q => !q.label.trim()))) {
      toast.error("All questions must have text");
      return false;
    }
    if (groups.some(group => group.questions.some(q => (q.type === 'radio' || q.type === 'checkbox') && q.options && q.options.some(opt => !opt.name.trim())))) {
      toast.error("All options must have text");
      return false;
    }
    // Only require sleeve_uuid for smart questionnaires
    if (questionnaireType === 'smart' && !sleeve_uuid) {
      toast.error("Sleeve UUID is required");
      return false;
    }
    return true;
  };

  const onSaveForm = async (e) => {
    e.preventDefault();
    onStoreForm(e, 1);
  };

  const onSaveAddForm = async (e) => {
    e.preventDefault();
    onStoreForm(e, 2);
  };

  const onStoreForm = async (e, type) => {
    e.preventDefault();

    if (!validateFormData()) return;

    setLoading(true);
    setIsSaving(true);

    try {
      const formData = {
        ...(questionnaireType === 'smart' ? { sleeve_uuid } : {}),
        form_name: formName,
        groups: groups.map((group) => {
          const groupData = {
            group_name: group.name,
            questions: group.questions.map((question) => {
              const questionType = QUESTION_TYPES.find(type => type.value === question.type);
              const questionData = {
                question_text: question.label,
                question_type_id: questionType.id,
                is_required: !!question.is_required
              };

              // For edit mode, include existing question_id if available
              if (editMode && question.question_id) {
                questionData.question_id = question.question_id;
              }

              // Handle options for radio and checkbox questions
              if (question.type === "radio" || question.type === "checkbox") {
                questionData.options = question.options ? question.options.map((option) => {
                  const optionData = { name: option.name };
                  // Include option_id for existing options in edit mode
                  if (editMode && option.option_id) {
                    optionData.option_id = option.option_id;
                  }
                  return optionData;
                }) : [];
              }
              return questionData;
            })
          };

          // For edit mode, include existing group_id if available
          if (editMode && group.group_id) {
            groupData.group_id = group.group_id;
          }

          return groupData;
        })
      };

      // For normal questionnaire edit, include form_uuid in the payload
      if (questionnaireType === 'normal' && editMode && editFormData?.form_uuid) {
        formData.form_uuid = editFormData.form_uuid;
      }

      let response;
      if (editMode && (editFormData?.form_id || editFormData?.form_uuid)) {
        // When editing a form, include the form_id/form_uuid but still respect the questionnaire type
        if (questionnaireType === 'normal') {
          response = await editNormalQuestionaireForm({ ...formData, form_uuid: editFormData.form_uuid });
        } else {
          response = await editSmartQuestionaireForm({ ...formData, form_id: editFormData.form_id });
        }
      } else {
        // For new forms, use the appropriate API based on questionnaire type
        if (questionnaireType === 'normal') {
          response = await createNormalQuestionaireForm(formData);
        } else {
          response = await createSmartQuestionaireForm(formData);
        }
      }

      if (
        (typeof response.data.success !== "undefined" && response.data.success) ||
        (typeof response.data.message === "string" && response.data.message.toLowerCase().includes("success"))
      ) {
        if (type === 1) {
          onClose();
        }
        clearForm();
        if (onFormSaved) onFormSaved();
        toast.success(editMode ? "Form updated successfully!" : "Form created successfully!");
      } else {
        toast.error(response.data.message || "Failed to save form. Please try again.");
      }
    } catch (error) {
      console.error("Error saving form:", error);
      toast.error(error.response?.data?.message || error.message || "Error saving form. Please try again.");
    } finally {
      setLoading(false);
      setIsSaving(false);
    }
  };

  const handleGroupEdit = (idx) => {
    setEditingGroupIdx(idx);
    setEditingGroupName(groups[idx].name);
    setSelectedGroupIdx(idx);
  };

  // Helper to reorder questions array
  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  // Handle drag end for questions
  const onDragEnd = (result) => {
    if (!result.destination) return;
    if (selectedGroupIdx === null) return;
    const { source, destination } = result;
    if (source.index === destination.index) return;
    setGroups(groups => groups.map((g, i) => {
      if (i !== selectedGroupIdx) return g;
      return {
        ...g,
        questions: reorder(g.questions, source.index, destination.index)
      };
    }));
  };

  const reorderGroups = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const onGroupDragEnd = (result) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;
    setGroups(groups => reorderGroups(groups, result.source.index, result.destination.index));
    if (selectedGroupIdx === result.source.index) setSelectedGroupIdx(result.destination.index);
    else if (
      selectedGroupIdx > result.source.index && selectedGroupIdx <= result.destination.index
    ) setSelectedGroupIdx(selectedGroupIdx - 1);
    else if (
      selectedGroupIdx < result.source.index && selectedGroupIdx >= result.destination.index
    ) setSelectedGroupIdx(selectedGroupIdx + 1);
  };

  return (
    <>
      {loading && <div className="page-loading">
        <img src="/images/loader.gif" alt="loader" />
      </div>}
      {/* Backdrop */}
      {show && <div className="offcanvas-backdrop show"></div>}
      <div className={`offcanvas offcanvas-end w-80${show ? ' show' : ''}`} data-bs-scroll="false" tabIndex={-1}
        data-bs-keyboard="true" data-bs-backdrop="static" id="formBuilderOffcanvas" aria-labelledby="formBuilderOffcanvasLabel" >
        <div className="offcanvas-header border-bottom">
          <h4 id="MakeFormLabel" className="mb-0 d-flex align-items-center">
            {(editMode && editFormData?.form_id) ? 'Edit Questionnaire' : 'Create Questionnaire'}
          </h4>
          <button
            ref={closeButtonRef}
            className="btn-close"
            onClick={onClose}
            type="button"
            aria-label="Close"
          >
            <i className="bi bi-x-circle"></i>
          </button>
        </div>

        <div className="offcanvas-body p-0 smartquestionaire-offcanvas-body">
          <div className="d-flex h-100">
            <div className="form-builder-sidebar border-end p-4 form-builder-sidebar-width">
              <div className="mb-4">
                <label htmlFor="formNameInput" className="form-label fw-bold">
                  Questionnaire Form Name
                  <i className="bi bi-info-circle ms-2 text-muted cursor-pointer" data-toggle="tooltip" title="Enter the name of your Questionnaire form. This title will be shown to users."></i>
                </label>
                <input
                  id="formNameInput"
                  type="text"
                  className="form-control"
                  placeholder="Enter form name"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  aria-required="true"
                />
              </div>

              <div className="mb-3">
                <label htmlFor="groupNameInput" className="form-label fw-bold">
                  Question Groups
                  <i className="bi bi-info-circle ms-2 text-muted cursor-pointer" data-toggle="tooltip" title="Add groups to organize your questions into different sections.."></i>
                </label>
                <div className="input-group mb-3 custom-group-input">
                  <input
                    id="groupNameInput"
                    type="text"
                    className={`form-control no-left-radius customInputStyle ${groupNameError ? "is-invalid" : ""} group-input-style`}
                    placeholder="New group name"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAddGroup()}
                    aria-required="true"
                  />
                  <button
                    className="btn btn-primary no-right-radius d-flex align-items-center justify-content-center customButtonStyle group-btn-style"
                    onClick={handleAddGroup}
                    aria-label="Add new group"
                  >
                    <i className="bi bi-plus-lg"></i>
                  </button>
                  {groupNameError && <div className="invalid-feedback d-block">{groupNameError}</div>}
                </div>
              </div>

              {/* GROUPS DRAGGABLE LIST */}
              <DragDropContext onDragEnd={onGroupDragEnd}>
                <Droppable droppableId="groups-list">
                  {(provided) => (
                    <div className="group-list-container" ref={provided.innerRef} {...provided.droppableProps}>
                      {groups.map((group, idx) => (
                        <Draggable key={`group-${group.name}-${idx}`} draggableId={`group-${group.name}-${idx}`} index={idx}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`group-item${selectedGroupIdx === idx ? ' selected' : ''}${snapshot.isDragging ? ' dragging' : ''}`}
                              onClick={() => handleSelectGroup(idx)}
                              role="button"
                              aria-selected={selectedGroupIdx === idx}
                            >
                              <div className="group-item-content">
                                {/* Group info section */}
                                <div className="group-info">
                                  {editingGroupIdx === idx ? (
                                    <input
                                      type="text"
                                      className="group-edit-input"
                                      value={editingGroupName}
                                      autoFocus
                                      onChange={e => setEditingGroupName(e.target.value)}
                                      onBlur={() => {
                                        handleGroupNameChange(idx, editingGroupName);
                                        setEditingGroupIdx(null);
                                      }}
                                      onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                          handleGroupNameChange(idx, editingGroupName);
                                          setEditingGroupIdx(null);
                                        } else if (e.key === 'Escape') {
                                          setEditingGroupIdx(null);
                                        }
                                      }}
                                    />
                                  ) : (
                                    <>
                                      <div className="group-name">{group.name}</div>
                                      <div className="group-count">{group.questions.length} question(s)</div>
                                    </>
                                  )}
                                </div>

                                {/* Action buttons - always visible but more subtle when not selected */}
                                <div className={`group-actions ${selectedGroupIdx === idx ? 'visible' : 'minimized'}`}>
                                  <button
                                    className="group-action-btn edit-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleGroupEdit(idx);
                                    }}
                                    title="Edit Group"
                                  >
                                    <i className="bi bi-pencil"></i>
                                    {selectedGroupIdx === idx && <span></span>}
                                  </button>

                                  <button
                                    className="group-action-btn delete-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteGroup(idx);
                                    }}
                                    title={`Delete group ${group.name}`}
                                  >
                                    <i className="bi bi-trash"></i>
                                    {selectedGroupIdx === idx && <span></span>}
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>

            </div>

            <div className="form-builder-content flex-grow-1 p-4">
              {selectedGroupIdx === null ? (
                <div className="text-center py-5">
                  <i className="bi bi-layout-text-window-reverse display-1 text-muted"></i>
                  <h4 className="mt-3">Select a Group</h4>
                  <p className="text-muted">Choose a group from the sidebar to start adding questions</p>
                </div>
              ) : (
                <div className="questions-container">
                  <h5 className="mb-4">
                    {groups[selectedGroupIdx].name} - Questions
                  </h5>
                  <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="questions-list">
                      {(provided) => (
                        <div className="questions-list" ref={provided.innerRef} {...provided.droppableProps}>
                          {groups[selectedGroupIdx].questions.map((question, qIdx) => (
                            <Draggable key={`question-${selectedGroupIdx}-${qIdx}`} draggableId={`question-${selectedGroupIdx}-${qIdx}`} index={qIdx}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className={`question-card border rounded p-4 mb-3${snapshot.isDragging ? ' dragging' : ''} question-card-rel`}
                                >
                                  <div className="row align-items-center">
                                    {/* Drag handle icon - only this is draggable */}
                                    <div className="col-auto d-flex mt-1 pe-0 me-2" style={{ cursor: 'grab' }} {...provided.dragHandleProps}>
                                      <DragHandleIcon />
                                    </div>
                                    <div className="col ps-0">
                                      <div className="row">
                                        <div
                                          className="question-delete-x"
                                          onClick={() => handleDeleteQuestion(qIdx)}
                                          title="Delete question"
                                        >
                                          &times;
                                        </div>
                                        <div className="col-md-4">
                                          <label
                                            htmlFor={`questionType${qIdx}`}
                                            className="form-label fw-bold mb-2"
                                          >
                                            Question Type
                                          </label>
                                          <select
                                            id={`questionType${qIdx}`}
                                            className="form-select"
                                            value={question.type}
                                            onChange={(e) => handleQuestionChange(qIdx, 'type', e.target.value)}
                                          >
                                            <option value="" disabled>Select question type</option>
                                            {QUESTION_TYPES.map((type) => (
                                              <option key={type.value} value={type.value}>{type.label}</option>
                                            ))}
                                          </select>
                                        </div>
                                        <div className="col-md-8">
                                          <label htmlFor={`questionText${qIdx}`} className="form-label mb-2">Question Text</label>
                                          <div className="input-group">
                                            <input
                                              id={`questionText${qIdx}`}
                                              type="text"
                                              className="form-control no-left-radius"
                                              placeholder="Enter your question"
                                              value={question.label}
                                              onChange={(e) => handleQuestionChange(qIdx, 'label', e.target.value)}
                                              aria-required="true"
                                            />
                                          </div>
                                        </div>
                                      </div>

                                      {(question.type === 'radio' || question.type === 'checkbox') && (
                                        <div className="mt-3">
                                          <label className="form-label">Options</label>
                                          <div className="options-container">
                                            {question.options?.map((option, optionIdx) => (
                                              <div key={optionIdx} className="input-group mb-2">
                                                <span className="input-group-text option-input-style">
                                                  {question.type === 'radio' && (
                                                    <input type="radio" name={`radio-group-${qIdx}`} checked={question.selectedOption === optionIdx} onChange={() => handleRadioSelect(qIdx, optionIdx)} />
                                                  )}
                                                  {question.type === 'checkbox' && (
                                                    <input type="checkbox" name={`checkbox-group-${qIdx}`} checked={Array.isArray(question.selectedOption) && question.selectedOption.includes(optionIdx)} onChange={() => handleCheckboxSelect(qIdx, optionIdx)} />
                                                  )}
                                                </span>
                                                <input
                                                  type="text"
                                                  className="form-control rounded-0 option-input-style"
                                                  placeholder={`Option ${optionIdx + 1}`}
                                                  value={option.name}
                                                  onChange={(e) => handleOptionChange(qIdx, optionIdx, e.target.value)}
                                                  aria-label={`Option ${optionIdx + 1}`}
                                                />
                                                {question.options.length === 1 ? (
                                                  <button
                                                    className="btn btn-outline-danger no-right-radius option-input-style"
                                                    onClick={() => handleDeleteOption(qIdx, optionIdx)}
                                                    aria-label={`Remove option`}
                                                  >
                                                    <i className="bi bi-x-lg"></i>
                                                  </button>
                                                ) : (
                                                  <button
                                                    className="btn btn-danger no-right-radius option-input-style"
                                                    onClick={() => handleDeleteOption(qIdx, optionIdx)}
                                                    aria-label={`Delete option ${optionIdx + 1}`}
                                                  >
                                                    <i className="bi bi-trash bg-transparent"></i>
                                                  </button>
                                                )}
                                              </div>
                                            ))}
                                            <button
                                              className="btn btn-outline-primary btn-sm"
                                              onClick={() => handleAddOption(qIdx)}
                                              aria-label="Add new option"
                                            >
                                              <i className="bi bi-plus-lg me-1"></i>
                                              Add Option
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="d-flex justify-content-end align-items-center mt-3">
                                    <div className="form-check d-flex align-items-center gap-2 cur-pointer">
                                      <input
                                        className="form-check-input cur-pointer"
                                        type="checkbox"
                                        id={`requiredCheck-${selectedGroupIdx}-${qIdx}`}
                                        checked={!!question.is_required}
                                        onChange={() => handleRequiredToggle(qIdx)}
                                        data-bs-toggle="tooltip"
                                        data-bs-placement="top"
                                        title={question.is_required ? 'Required' : 'Not Required'}
                                      />
                                      <label
                                        className="form-check-label user-select-none mb-0"
                                        htmlFor={`requiredCheck-${selectedGroupIdx}-${qIdx}`}
                                        style={{ cursor: 'pointer' }}
                                      >
                                        Required
                                      </label>
                                      <i
                                        className="bi bi-info-circle ms-1"
                                        data-toggle="tooltip"
                                        data-bs-placement="top"
                                        title="If checked, this question will be required for the user to answer."
                                        tabIndex={0}
                                      ></i>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                          <div
                            className="question-card border rounded p-4 mb-3 add-question-blur-card"
                          >
                            <div className="row align-items-center add-question-blur-row">
                              <div className="col-md-4">
                                <label htmlFor="questionType" className="form-label fw-bold mb-2">Question Type</label>
                                <select className="form-select" disabled>
                                  <option>Text</option>
                                </select>
                              </div>
                              <div className="col-md-8">
                                <label htmlFor="questionText" className="form-label fw-bold mb-2">Question Text</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Question Text"
                                  disabled
                                />
                              </div>
                            </div>
                            <div className="row mt-3">
                              <div className="col-12 d-flex justify-content-center">
                                <button
                                  className="btn btn-outline-primary add-question-btn-style"
                                  onClick={handleAddQuestion}
                                  tabIndex={0}
                                  aria-label="Add new question"
                                >
                                  <i className="bi bi-plus-lg me-2"></i>
                                  Add Question
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="offcanvas-footer border-top pe-3 py-2">
          <div className="d-flex justify-content-end gap-2">
            <button
              className="btn btn-light"
              onClick={onClose}
              disabled={isSaving}
              type="button"
              aria-label="Cancel form creation"
            >
              Cancel
            </button>
            <button
              className="btn btn-outline-primary"
              onClick={onSaveAddForm}
              disabled={isSaving}
              type="button"
              aria-label="Save and add another form"
            >
              Save & Add Another
            </button>
            <button
              className="btn btn-primary"
              onClick={onSaveForm}
              disabled={isSaving}
              type="button"
              aria-label="Save form"
            >
              Save
            </button>
          </div>
        </div>
      </div >
    </>
  );
}

export default FormBuilderOffcanvas;